import useUser from '@/hooks/useUser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BiSolidMessageSquareDetail } from 'react-icons/bi';
import { FaAddressBook } from 'react-icons/fa';
import { GoPlus } from 'react-icons/go';
import { MdOutlineUpgrade, MdSpaceDashboard } from 'react-icons/md';
import { RiCalendarScheduleFill } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useDashboardNewAppointmentModal from '../../../hooks/useDashboardNewAppointmentModal';
import useDashboardNewBusinessModal from '../../../hooks/useDashboardNewBusinessModal';
import useDashboardNewServiceModal from '../../../hooks/useDashboardNewServiceModal';
import useDashboardNewTempHourModal from '../../../hooks/useDashboardNewTempHourModal';
import usePartnerNewContactModal from '../../../hooks/usePartnerNewContactModal';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';

const DashboardBottomMobileMenu = () => {
  const location = useLocation();
  const { navigate, toLocalizedRoute, isSameRoute, isChildRoute } =
    useAppNavigation();
  const dashboardNewAppointmentModal = useDashboardNewAppointmentModal();
  const newContactModal = usePartnerNewContactModal();
  const newServiceModal = useDashboardNewServiceModal();
  const newBusinessModal = useDashboardNewBusinessModal();
  const newTempHourModal = useDashboardNewTempHourModal();
  const [newMessagesAmount, setNewMessagesAmount] = useState<number>(0);
  const {
    hasReachedActiveLimit,
    hasReachedContactsLimit,
    hasReachedBusinessesLimit,
    hasReachedServicesLimit,
  } = useUser();

  const { conversations } = useSelector((state: RootReducerType) => state.chat);

  const onPlusClick = () => {
    if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
      newContactModal.onOpen();
    } else if (isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
      newServiceModal.onOpen();
    } else if (
      isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)
    ) {
      newBusinessModal.onOpen();
    } else if (
      isSameRoute(
        location.pathname,
        ROUTES.DASHBOARD_ROUTE_TEMP_CLOSING_HOURS,
      ) ||
      isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TEMP_OPENING_HOURS)
    ) {
      newTempHourModal.onOpen();
    } else {
      dashboardNewAppointmentModal.onOpen();
    }
  };

  const onPlanChange = useCallback(() => {
    navigate(toLocalizedRoute(ROUTES.PRICES_ROUTE));
  }, [navigate, toLocalizedRoute]);

  const showUpgradeButton = useMemo<boolean>(() => {
    const isContactPage = isSameRoute(
      location.pathname,
      ROUTES.DASHBOARD_ROUTE_CONTACTS,
    );
    const isBusinessesPage = isSameRoute(
      location.pathname,
      ROUTES.DASHBOARD_ROUTE_BUSINESS,
    );
    const isServicesPage = isSameRoute(
      location.pathname,
      ROUTES.DASHBOARD_ROUTE_SERVICE,
    );

    if (isContactPage && hasReachedContactsLimit) {
      return true;
    }
    if (isBusinessesPage && hasReachedBusinessesLimit) {
      return true;
    }
    if (isServicesPage && hasReachedServicesLimit) {
      return true;
    }
    if (!isContactPage && hasReachedActiveLimit) {
      return true;
    }

    return false;
  }, [
    hasReachedActiveLimit,
    hasReachedContactsLimit,
    hasReachedBusinessesLimit,
    hasReachedServicesLimit,
    location,
    toLocalizedRoute,
  ]);

  useEffect(() => {
    if (conversations) {
      let msgAmounts = 0;
      for (let index = 0; index < conversations.conversations.length; index++) {
        const conversation = conversations.conversations[index];
        msgAmounts += conversation.data.new_messages;
      }

      if (newMessagesAmount !== msgAmounts) {
        setNewMessagesAmount(msgAmounts);
      }
    }
  }, [conversations]);

  const pageHidden = useMemo<boolean>(() => {
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)) {
      return true;
    }
    if (isChildRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_TREATMENTS)) {
      return true;
    }

    return false;
  }, [location, isChildRoute]);

  return (
    <div
      className={`${
        pageHidden ? 'hidden' : 'flex'
      } h-[90px] max-h-[90px] flex-row items-end justify-center bg-gradient-to-t from-white lg:hidden`}
    >
      <div className="relative flex grow flex-row items-center justify-evenly rounded-t-xl bg-sky-600 py-2 text-white lg:hidden">
        <button
          id="dashboard-bottom-menu-item-overview"
          key="dashboard-bottom-menu-item-overview"
          aria-label="dashboard-bottom-menu-item-overview"
          data-test-id="dashboard-bottom-menu-item-overview"
          data-umami-event="dashboard-bottom-menu-item-overview"
          className={`${
            isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_OVERVIEW)
              ? 'cursor-pointer text-white opacity-50'
              : 'text-sky-700 hover:text-white'
          } rounded-full p-2`}
          disabled={isSameRoute(
            location.pathname,
            ROUTES.DASHBOARD_ROUTE_OVERVIEW,
          )}
          onClick={() =>
            navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_OVERVIEW))
          }
          type="button"
          onKeyDown={() =>
            navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_OVERVIEW))
          }
        >
          <MdSpaceDashboard size={32} />
        </button>
        <button
          id="dashboard-bottom-menu-item-appointments"
          key="dashboard-bottom-menu-item-appointments"
          aria-label="dashboard-bottom-menu-item-appointments"
          data-test-id="dashboard-bottom-menu-item-appointments"
          data-umami-event="dashboard-bottom-menu-item-appointments"
          className={`${
            isSameRoute(
              location.pathname,
              ROUTES.DASHBOARD_ROUTE_APPOINTMENTS,
            ) || isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)
              ? 'cursor-pointer text-white opacity-50'
              : 'text-sky-700 hover:text-white'
          } rounded-full p-2`}
          disabled={
            isSameRoute(
              location.pathname,
              ROUTES.DASHBOARD_ROUTE_APPOINTMENTS,
            ) || isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)
          }
          onClick={() =>
            navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_APPOINTMENTS))
          }
          type="button"
        >
          <RiCalendarScheduleFill size={34} />
        </button>
        <span className="rounded-full p-2 px-6 text-sky-700 opacity-0">
          <GoPlus size={32} />
        </span>
        <button
          id="dashboard-bottom-menu-item-clients"
          key="dashboard-bottom-menu-item-clients"
          aria-label="dashboard-bottom-menu-item-clients"
          data-test-id="dashboard-bottom-menu-item-clients"
          data-umami-event="dashboard-bottom-menu-item-clients"
          className={`${
            isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)
              ? 'cursor-pointer text-white opacity-50'
              : 'text-sky-700 hover:text-white'
          } rounded-full p-2`}
          disabled={isSameRoute(
            location.pathname,
            ROUTES.DASHBOARD_ROUTE_CONTACTS,
          )}
          onClick={() =>
            navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_CONTACTS))
          }
          type="button"
          onKeyDown={() =>
            navigate(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_CONTACTS))
          }
        >
          <FaAddressBook size={32} />
        </button>
        <button
          id="dashboard-bottom-menu-item-chat"
          key="dashboard-bottom-menu-item-chat"
          aria-label="dashboard-bottom-menu-item-chat"
          data-test-id="dashboard-bottom-menu-item-chat"
          data-umami-event="dashboard-bottom-menu-item-chat"
          className={`relative ${
            isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE)
              ? 'cursor-pointer text-white opacity-50'
              : 'text-sky-700 hover:text-white'
          } rounded-full p-2`}
          disabled={isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE)}
          onClick={() => navigate(toLocalizedRoute(ROUTES.MESSAGES_ROUTE))}
          type="button"
          onKeyDown={() => navigate(toLocalizedRoute(ROUTES.MESSAGES_ROUTE))}
        >
          <BiSolidMessageSquareDetail size={32} />
          {newMessagesAmount > 0 && (
            <span className="absolute right-0 top-0 min-w-[18px] rounded-full bg-white px-[3px] py-px text-xs font-semibold text-sky-700 shadow">
              {newMessagesAmount > 99 ? '99+' : newMessagesAmount}
            </span>
          )}
        </button>
        {showUpgradeButton ? (
          <span className="group absolute bottom-0 z-10 flex cursor-pointer flex-col items-center justify-center rounded-full bg-sky-600 p-2">
            <button
              id="dashboard-bottom-menu-item-plan-upgrade"
              key="dashboard-bottom-menu-item-plan-upgrade"
              aria-label="dashboard-bottom-menu-item-plan-upgrade"
              data-test-id="dashboard-bottom-menu-item-plan-upgrade"
              data-umami-event="dashboard-bottom-menu-item-plan-upgrade"
              onClick={onPlanChange}
              type="button"
              onKeyDown={onPlanChange}
              className="rounded-full bg-white p-3 text-sky-600 group-hover:bg-sky-700 group-hover:text-white"
            >
              <MdOutlineUpgrade size={40} />
            </button>
          </span>
        ) : (
          <span className="group absolute bottom-0 z-10 flex cursor-pointer flex-col items-center justify-center rounded-full bg-sky-600 p-2">
            <button
              id="dashboard-bottom-menu-item-new-appointment"
              key="dashboard-bottom-menu-item-new-appointment"
              aria-label="dashboard-bottom-menu-item-new-appointment"
              data-test-id="dashboard-bottom-menu-item-new-appointment"
              data-umami-event="dashboard-bottom-menu-item-new-appointment"
              onClick={onPlusClick}
              type="button"
              onKeyDown={onPlusClick}
              className="rounded-full bg-white p-3 text-sky-600 group-hover:bg-sky-700 group-hover:text-white"
            >
              <GoPlus size={40} />
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default DashboardBottomMobileMenu;
